import {
  CatalystInsightCategory,
  CatalystSubcategories,
} from '@api/api/catalyst-insights';
import { KgInsightCategory } from '@api/api/kg-insights';
import { Icon } from '@design/components/icon/Icon';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useMarketCatalystDrawer } from '@terminal/widgets/market-catalyst-drawer/useMarketCatalystDrawer';
import clsx from 'clsx';
import React from 'react';

import { CatalystInsightTooltip } from '../catalyst-insight-tooltip/CatalystInsightTooltip';
import { useCatalystDrawer } from '../useCatalystDrawer';
import styles from './CatalystInsightIcon.module.scss';
import { getCatalystInsightIconProps } from './utils';

const COMPANY_CATALYSTS = 'company';

export interface CatalystInsightIconProps {
  catalystInsightId: string;
  subCategory?: CatalystSubcategories;
  size?: number;
  category: CatalystInsightCategory | KgInsightCategory;
  enableHover?: boolean;
  isBordered?: boolean;
  identifier?: string;
  customSize?: { width: number; height: number };
  isDisabled?: boolean;
  source: PosthogTrackingSource;
  isClickable?: boolean;
}

export const CatalystInsightIcon = ({
  catalystInsightId,
  category,
  subCategory,
  size,
  enableHover = true,
  isBordered = true,
  identifier,
  customSize,
  isDisabled,
  source,
  isClickable = true,
}: CatalystInsightIconProps) => {
  const { openDrawer: openCompanyCatalystDrawer } = useCatalystDrawer();
  const { openDrawer: openMarketCatalystDrawer } = useMarketCatalystDrawer();
  const isCompanyCatalyst =
    category === COMPANY_CATALYSTS || category === 'company_catalyst';
  const iconProps = getCatalystInsightIconProps({
    isBordered,
    isDisabled,
    isCompanyCatalyst,
  });
  const { trackInsightIconClicked } = usePosthogEvents();

  const handleClick = (e: React.MouseEvent<SVGSVGElement>) => {
    e.stopPropagation();

    if (!identifier) {
      return;
    }

    trackInsightIconClicked({
      source,
      type: isCompanyCatalyst ? 'company_catalyst' : 'market_catalyst',
      insightId: catalystInsightId,
      entities: isCompanyCatalyst ? [identifier] : undefined,
      themeIds: !isCompanyCatalyst ? [identifier] : undefined,
    });

    if (isCompanyCatalyst) {
      openCompanyCatalystDrawer({
        type: 'company',
        entityTag: identifier,
        insightId: catalystInsightId,
        source,
      });
    } else if (subCategory) {
      openMarketCatalystDrawer({
        type: subCategory,
        themeId: identifier,
        insightId: catalystInsightId,
        source,
      });
    }
  };

  return (
    <TooltipContainer
      label={
        <CatalystInsightTooltip
          isCompanyCatalyst={isCompanyCatalyst}
          subCategory={subCategory}
          iconProps={iconProps}
        />
      }
      placement="top-start"
      offsetY={-5}
      offsetX={8}
      hasMaxWidth={false}
      disabled={!enableHover || isDisabled}
      inPortal
      withSafePolygon
    >
      <Icon
        {...iconProps}
        {...(customSize
          ? { width: customSize.width, height: customSize.height }
          : { size })}
        className={clsx(styles.styledIcon, {
          [styles.enableHover]: enableHover && isClickable,
          [styles.isDisabled]: isDisabled,
        })}
        onClick={isClickable ? handleClick : undefined}
      />
    </TooltipContainer>
  );
};
